import { motion } from "framer-motion";
import { HeartPulse, Users, Building2, ArrowRight } from "lucide-react";

const scrollToBooking = () => {
  const el = document.getElementById("booking");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const SECTORS = [
  {
    testid: "sector-mental-health",
    Icon: HeartPulse,
    label: "Mental Health & Behavioral Providers",
    pain: "Clinician burnout, disengaged clients, and treatment plateaus quietly drain your practice.",
    solution:
      "Casey Muze equips therapists, treatment centers, and behavioral health teams with bilateral-rhythm and somatic regulation tools that re-engage clients, lower staff burnout, and add a body-based modality your clients can feel — not just talk through.",
    keywords: "Somatic regulation workshops · Clinician wellbeing keynotes",
  },
  {
    testid: "sector-community",
    Icon: Users,
    label: "Community Organizations & Nonprofits",
    pain: "Disconnection, generational stress, and hard-to-reach populations stall your mission.",
    solution:
      "From schools and youth programs to faith and neighborhood groups, Casey's interactive drumming experiences build trust, belonging, and emotional resilience — reaching people that words and worksheets never could.",
    keywords: "Community healing circles · Youth resilience programs",
  },
  {
    testid: "sector-corporate",
    Icon: Building2,
    label: "Corporate Teams & Forte Building",
    pain: "Silent stress, low morale, and siloed teams quietly erode performance and retention.",
    solution:
      "Casey's rhythm-based forte-building keynotes turn stressed, disconnected employees into one synchronized team — lifting focus, communication, and culture in a single unforgettable session your people actually remember.",
    keywords: "Corporate team-building drumming · Employee wellbeing keynotes",
  },
];

const Card = ({ sector, index }) => {
  const { Icon } = sector;
  return (
    <motion.div
      data-testid={sector.testid}
      className="group flex flex-col rounded-3xl border border-[#AEE6D5]/15 bg-[#0A2E2C]/60 p-8 transition-colors hover:border-[#D4AF37]/40"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.7, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
    >
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#D4AF37]/12 text-[#D4AF37]">
        <Icon className="h-6 w-6" strokeWidth={1.6} />
      </span>
      <h3 className="mt-6 font-serif text-2xl font-light leading-tight tracking-tight text-[#F8FAFC]">
        {sector.label}
      </h3>
      <p className="mt-4 text-sm font-medium leading-relaxed text-[#AEE6D5]">{sector.pain}</p>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-[#F8FAFC]/75">{sector.solution}</p>
      <p className="mt-5 font-mono text-[0.6rem] uppercase tracking-[0.16em] text-[#F8FAFC]/45">
        {sector.keywords}
      </p>
      <button
        onClick={scrollToBooking}
        data-testid={`${sector.testid}-cta`}
        data-cursor="org"
        className="mt-6 inline-flex items-center gap-2 self-start rounded-full border border-[#D4AF37]/50 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-[#0A2E2C]"
      >
        Book a session
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
      </button>
    </motion.div>
  );
};

export const WhoIServe = () => (
  <section className="relative z-10 bg-[#0B3B3C] px-6 py-20 sm:px-10 lg:px-12 lg:py-32" data-testid="who-i-serve">
    <div className="mx-auto max-w-6xl">
      <div className="mb-14 max-w-3xl">
        <p className="font-mono text-[0.7rem] uppercase tracking-[0.3em] text-[#D4AF37]">Who Casey serves</p>
        <h2 className="mt-4 font-serif text-4xl font-light leading-[1.05] tracking-tight text-[#F8FAFC] sm:text-5xl">
          Rhythm-based keynotes & workshops for the teams who <span className="italic text-[#AEE6D5]">carry the load.</span>
        </h2>
        <p className="mt-6 text-base leading-relaxed text-[#F8FAFC]/75">
          Whether you run a mental health practice, lead a community organization, or manage a corporate team,
          the same problem shows up: people are stressed, stretched, and out of sync. Casey Muze turns that
          static into shared rhythm — delivering measurable engagement, connection, and resilience.
        </p>
      </div>
      <div className="grid gap-6 md:grid-cols-3">
        {SECTORS.map((s, i) => <Card key={s.testid} sector={s} index={i} />)}
      </div>
    </div>
  </section>
);
